import React, { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Waves, Anchor, Home as HomeIcon, Heart, Users, Droplet, Sparkles, PawPrint, Building, Star } from "lucide-react";
import api from "@/lib/api";

const ICON_MAP = {
  Waves, Lighthouse: Anchor, Home: HomeIcon, Heart, Users, Droplet, Sparkles, PawPrint, Building, Star,
};

export default function CategoryBar({ value, onChange, sticky = false }) {
  const [cats, setCats] = useState([]);
  const scrollerRef = useRef(null);
  const [canL, setCanL] = useState(false);
  const [canR, setCanR] = useState(true);

  useEffect(() => {
    let on = true;
    api.get("/categories").then(({ data }) => on && setCats(data || [])).catch(() => {});
    return () => { on = false; };
  }, []);

  const updateArrows = () => {
    const el = scrollerRef.current;
    if (!el) return;
    setCanL(el.scrollLeft > 8);
    setCanR(el.scrollLeft + el.clientWidth < el.scrollWidth - 8);
  };
  useEffect(() => {
    updateArrows();
    const el = scrollerRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateArrows);
    window.addEventListener("resize", updateArrows);
    return () => {
      el.removeEventListener("scroll", updateArrows);
      window.removeEventListener("resize", updateArrows);
    };
  }, [cats]);

  const scrollBy = (px) => {
    scrollerRef.current?.scrollBy({ left: px, behavior: "smooth" });
  };

  if (!cats.length) return null;

  return (
    <div className={`relative ${sticky ? "sticky top-16 sm:top-20 z-30 bg-white/90 backdrop-blur-md border-b border-black/5" : ""}`} data-testid="category-bar">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 relative">
        {canL && (
          <button
            type="button"
            onClick={() => scrollBy(-280)}
            className="hidden sm:flex absolute left-2 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-white border border-black/10 rounded-full items-center justify-center shadow"
            aria-label="Défiler à gauche"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
        )}
        <div
          ref={scrollerRef}
          className="flex gap-6 sm:gap-8 overflow-x-auto scrollbar-hide py-3 sm:py-4 px-1"
          style={{ scrollbarWidth: "none" }}
        >
          {/* "Tous" pseudo-chip */}
          <CatChip
            label="Tous"
            Icon={Sparkles}
            active={!value}
            onClick={() => onChange?.(null)}
            testid="cat-all"
          />
          {cats.map((c) => {
            const Icon = ICON_MAP[c.icon] || HomeIcon;
            return (
              <CatChip
                key={c.key}
                label={c.label}
                Icon={Icon}
                active={value === c.key}
                onClick={() => onChange?.(c.key === value ? null : c.key)}
                testid={`cat-${c.key}`}
              />
            );
          })}
        </div>
        {canR && (
          <button
            type="button"
            onClick={() => scrollBy(280)}
            className="hidden sm:flex absolute right-2 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-white border border-black/10 rounded-full items-center justify-center shadow"
            aria-label="Défiler à droite"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        )}
      </div>
      <style>{`.scrollbar-hide::-webkit-scrollbar{display:none}`}</style>
    </div>
  );
}

function CatChip({ label, Icon, active, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`shrink-0 flex flex-col items-center gap-1.5 pt-1 pb-2 border-b-2 transition-all ${
        active
          ? "border-[#0A0A0A] text-[#0A0A0A]"
          : "border-transparent text-neutral-500 hover:text-[#0A0A0A] hover:border-neutral-300"
      }`}
    >
      <Icon className="w-5 h-5" strokeWidth={1.7} />
      <span className="text-[11px] font-semibold whitespace-nowrap">{label}</span>
    </button>
  );
}
